"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { Shield, Building2, Loader2, CheckCircle, Clock, MessageSquare, FileText, AlertTriangle } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { usePortalSession } from "@/features/seo-portal/components/portal-session-provider";
import { TrustScoreCard } from "@/features/seo-portal/components/trust-score-card";
import { SeoDescriptionEditor } from "@/features/seo-portal/components/seo-description-editor";
import { ReviewManager, type ChopReview } from "@/features/seo-portal/components/review-manager";
import { updateSeoProfile } from "@/features/seo-portal/actions/update-seo-profile";
import type { SeoProfileData } from "@/features/seo-portal/components/seo-description-editor";
﻿
interface SeoCompany {
  id: string;
  name: string;
  inn: string | null;
  is_claimed: boolean;
  trust_score: number;
  seo_description: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  logo_url: string | null;
  slug: string | null;
}
﻿
function CabinetDashboard() {
  const { user, supabase } = usePortalSession();
  const [company, setCompany] = useState<SeoCompany | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState<ChopReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const loadCompany = useCallback(async () => {
    if (!supabase || !user) return;
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from("seo_companies")
        .select("*")
        .eq("owner_user_id", user.id)
        .maybeSingle();

      if (err) { setError("Ошибка загрузки данных"); return; }
      setCompany(data as unknown as SeoCompany);
    } catch { setError("Произошла ошибка при загрузке"); }
    finally { setLoading(false); }
  }, [supabase, user]);

  useEffect(() => {
    if (!user || !supabase) return;
    loadCompany();
  }, [user, supabase, loadCompany]);

  async function loadReviews(companyId: string) {
    setReviewsLoading(true);
    try {
      const res = await fetch(`/api/seo/cabinet-reviews?companyId=${companyId}`);
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews ?? []);
      }
    } catch { /* ignore */ }
    finally { setReviewsLoading(false); }
  }

  const handleSaveProfile = useCallback(async (data: SeoProfileData) => {
    if (!company) return { success: false, error: "Нет компании" };
    return await updateSeoProfile({
      companyId: company.id,
      seoDescription: data.seo_description,
      contactPhone: data.contact_phone || undefined,
      contactEmail: data.contact_email || undefined,
    });
  }, [company]);

  const handleRespondToReview = useCallback(async (reviewId: string, response: string) => {
    try {
      const res = await fetch("/api/seo/cabinet-reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewId, response }),
      });
      const data = await res.json();
      if (data.success) {
        setReviews((prev) => prev.map((r) => r.id === reviewId ? { ...r, owner_reply: response } : r));
        return { success: true };
      }
      return { success: false, error: data.error };
    } catch {
      return { success: false, error: "Ошибка отправки ответа" };
    }
  }, []);
﻿
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--portal-text-muted))]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 px-6 container mx-auto py-20">
        <nav className="mb-8">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
            <Link href="/" className="hover:text-[hsl(var(--portal-text))] transition-colors">Главная</Link>
            <span className="text-[hsl(var(--portal-text-dim))]">/</span>
            <span className="text-[hsl(var(--portal-text))]">Кабинет</span>
          </div>
        </nav>
        <div className="border border-[hsl(var(--portal-danger))]/30 bg-[hsl(var(--portal-danger))]/10 p-6 max-w-lg mx-auto text-center">
          <AlertTriangle className="w-8 h-8 text-[hsl(var(--portal-danger))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--portal-danger))] font-sans">{error}</p>
          <Button onClick={loadCompany} variant="outline" className="mt-4 rounded-none border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface-2))]">
            Повторить
          </Button>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="flex-1 px-6 container mx-auto py-20">
        <nav className="mb-8">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
            <Link href="/" className="hover:text-[hsl(var(--portal-text))] transition-colors">Главная</Link>
            <span className="text-[hsl(var(--portal-text-dim))]">/</span>
            <span className="text-[hsl(var(--portal-text))]">Кабинет</span>
          </div>
        </nav>
        <div className="max-w-lg mx-auto text-center border border-[hsl(var(--portal-border))] p-10">
          <Building2 className="w-12 h-12 text-[hsl(var(--portal-text-dim))] mx-auto mb-4" />
          <h1 className="text-xl font-bold uppercase tracking-wide mb-4">Нет подтверждённой компании</h1>
          <p className="text-sm text-[hsl(var(--portal-text-muted))] font-sans mb-6 leading-relaxed">
            Вы ещё не подтвердили свою охранную организацию.
            Подайте заявку на верификацию, чтобы получить доступ к кабинету.
          </p>
          <Link href="/claim">
            <Button className="rounded-none h-12 bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] uppercase tracking-[0.15em] text-sm font-bold px-8">
              Подтвердить компанию
            </Button>
          </Link>
        </div>
      </div>
    );
  }
﻿
  if (!company.is_claimed) {
    return (
      <div className="flex-1 px-6 container mx-auto py-20">
        <nav className="mb-8">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
            <Link href="/" className="hover:text-[hsl(var(--portal-text))] transition-colors">Главная</Link>
            <span className="text-[hsl(var(--portal-text-dim))]">/</span>
            <span className="text-[hsl(var(--portal-text))]">Кабинет</span>
          </div>
        </nav>
        <div className="max-w-lg mx-auto text-center border border-[hsl(var(--portal-accent-muted))] bg-[hsl(var(--portal-accent-muted))] p-10">
          <Clock className="w-12 h-12 text-[hsl(var(--portal-accent))] mx-auto mb-4" />
          <h1 className="text-xl font-bold uppercase tracking-wide mb-4 text-[hsl(var(--portal-accent))]">Заявка на рассмотрении</h1>
          <p className="text-sm text-[hsl(var(--portal-text-secondary))] font-sans mb-4 leading-relaxed">
            Ваша заявка на подтверждение профиля <strong className="text-[hsl(var(--portal-text))]">{company.name}</strong> находится на рассмотрении.
            Обычно это занимает до 24 часов.
          </p>
          <p className="text-[10px] text-[hsl(var(--portal-text-dim))] font-sans">
            После одобрения вы получите доступ к управлению профилем.
          </p>
        </div>
      </div>
    );
  }

  // Загружаем отзывы при первом рендере компании
  if (reviews.length === 0 && !reviewsLoading) {
    loadReviews(company.id);
  }

  return (
    <div className="flex-1 px-6 container mx-auto py-12">
      <nav className="mb-8">
        <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
          <Link href="/" className="hover:text-[hsl(var(--portal-text))] transition-colors">Главная</Link>
          <span className="text-[hsl(var(--portal-text-dim))]">/</span>
          <span className="text-[hsl(var(--portal-text))]">Кабинет</span>
        </div>
      </nav>

      <div className="flex items-center gap-3 mb-8">
        <Shield className="w-6 h-6 text-[hsl(var(--portal-accent))]" />
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-tight">{company.name}</h1>
          {company.slug && (
            <Link href={"/chop/" + company.slug} className="text-[10px] text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] transition-colors underline underline-offset-4">
              Публичная страница
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <SeoDescriptionEditor
            initialDescription={company.seo_description}
            initialContacts={{
              phone: company.contact_phone ?? undefined,
              email: company.contact_email ?? undefined,
            }}
            onSave={handleSaveProfile}
          />
          {reviewsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--portal-text-muted))]" />
            </div>
          ) : (
            <ReviewManager
              companyId={company.id}
              reviews={reviews}
              onRespond={handleRespondToReview}
            />
          )}
        </div>
        <div className="space-y-6">
          <TrustScoreCard
            trustScore={company.trust_score}
            verificationDone={company.is_claimed}
            reviewsCount={reviews.length}
            esgRating={null}
          />
        </div>
      </div>
    </div>
  );
}
﻿
export default function CabinetPage() {
  return <CabinetDashboard />;
}
